// Live-scoring indicator for a game row.
//
// Read-only on web: nothing here scores a game (the Official page is a plain
// final-score form), so this exists so a league admin at a desk can see that a
// game is already being kept — and, critically, on WHICH device.
//
// That last part is why `liveDeviceLabel` matters at all. A league owner in rec
// mode hands their login to a coach, so `officialName` is identical across every
// device that might be scoring; the device label is the only thing that
// distinguishes them. Mirrors the iOS LiveScoringStatus / LiveScorerCaption.

// Matches ForeignScoringSession.staleAfter on iOS. Sized off the client's 45s
// beacon heartbeat (two missed beats plus slack). Note a phone on an older App
// Store build sends no heartbeat at all and will read as stale while scoring
// fine — which is why this only softens the wording and never hides the badge.
const STALE_AFTER_MS = 120 * 1000;

function ageDescription(iso) {
  if (!iso) return null;
  const then = Date.parse(iso);
  if (Number.isNaN(then)) return null;
  const seconds = Math.max(0, Math.round((Date.now() - then) / 1000));
  if (seconds < 15) return "just now";
  if (seconds < 90) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  return `${Math.floor(minutes / 60)}h ago`;
}

function isStale(iso) {
  if (!iso) return true;
  const then = Date.parse(iso);
  return Number.isNaN(then) || Date.now() - then > STALE_AFTER_MS;
}

export function LiveGameBadge({ game }) {
  if (!game?.isLive || game.status === "completed") return null;
  const a = typeof game.liveScoreA === "number" ? game.liveScoreA : 0;
  const b = typeof game.liveScoreB === "number" ? game.liveScoreB : 0;
  return (
    <span>
      <span className="league-live-badge">
        <span className="league-live-dot" />
        Live
      </span>
      <span className="league-live-score">{a} – {b}</span>
    </span>
  );
}

// "Being scored on iPhone · A4F2 · 20s ago" under a live row.
export function LiveScorerLine({ game }) {
  if (!game?.isLive || game.status === "completed") return null;
  const age = ageDescription(game.liveUpdatedAt);
  const stale = isStale(game.liveUpdatedAt);
  const device = game.liveDeviceLabel;
  const suffix = age ? ` · ${age}` : "";

  // Prefer naming the person when there is one — a device label only helps when
  // the account can't tell you anything, which is exactly the shared-login case.
  let text;
  if (device) {
    text = `${stale ? "Was being scored" : "Being scored"} on ${device}${suffix}`;
  } else if (game.officialName) {
    text = `${stale ? "Was being scored" : "Being scored"} by ${game.officialName}${suffix}`;
  } else {
    text = `${stale ? "Was being scored" : "Being scored"}${suffix}`;
  }

  return (
    <div className={`league-live-scorer${stale ? "" : " league-live-elsewhere"}`}>
      {text}
    </div>
  );
}
